import { createTheme, Shadows, ThemeOptions } from '@mui/material/styles'
import palette from './palette'
import './theme.d.ts' // Import theme type extensions

// Typography variants based on audit findings
const typography = {
  fontFamily: ['Poppins', 'sans-serif'].join(','),
  allVariants: {
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: '0.04em',
  },
  // Predefined variants for common text styles
  h1: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '150%',
  },
  h2: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '150%',
  },
  h3: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '150%',
  },
  h4: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '150%',
  },
  body1: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '150%',
  },
  body2: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '150%',
  },
  button: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '100%',
    textTransform: 'none' as const,
  },
  caption: {
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '150%',
  },
  // Custom typography properties (added via theme extension)
  fontSize: {
    xs: '12px', // small text, error messages, notes
    sm: '14px', // default body text (MOST COMMON)
    md: '16px', // navigation, larger text
    lg: '20px', // H2 headings
    xl: '24px', // H1 headings
    xxl: '26px', // icon sizes
  },
  fontWeight: {
    light: 400, // mobile button
    regular: 500, // input text, regular (default)
    semiBold: 600, // buttons, headings (MOST COMMON)
    bold: 800, // emphasis
  },
  lineHeight: {
    compact: '100%', // buttons
    normal: '150%', // default body text (MOST COMMON)
    navigation: '20px', // navigation specific
    modal: '21px', // modal text specific
  },
} as const

// Spacing values from audit (using MUI's spacing function)
// MUI spacing: 1 unit = 8px by default
const spacing = {
  // Common padding patterns from audit
  padding: {
    button: '8px 16px', // badges, tabs (MOST COMMON)
    buttonAlt: '9px 16px', // tab variant
    input: '8px 14px 9px', // inputs
    inputAlt: '3px 10px', // compact inputs
    tableCell: '11px 16px 12px 0', // table cells
    modal: '16px 24px', // modals
    navigation: '20px 24px', // navigation
    formContainer: '45px 40px 40px', // form containers
    tabList: '3px 4px', // tab list container
  },
  // Common margin patterns from audit
  margin: {
    none: 0,
    h1Default: '14px 0', // H1 default
    xs: '4px',
    sm: '8px',
    md: '16px', // common marginTop
    lg: '20px',
    xl: '24px',
  },
} as const

// Border radius values from audit
const shape = {
  borderRadius: 4, // MUI default, matching most common usage
  borderRadiusVariants: {
    none: '0px', // explicitly no radius
    small: '4px', // default for buttons, inputs (MOST COMMON)
    medium: '6px', // tab containers
    large: '8px', // modals, form containers
    pill: '44px', // badges/pills
    circle: '100%', // circular elements
  },
} as const

// Standard MUI shadows array
const muiShadows: Shadows = [
  'none',
  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
  '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
  '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
  '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
  '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
  '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
  '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
  '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
  '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
  '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
  '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
  '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
  '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
  '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
  '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
  '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
  'rgba(0, 0, 0, 0.5)', // Custom shadow at index 23
  '0px 8px 24px rgba(34, 34, 34, 0.15)', // Custom modal shadow at index 24
]

// Custom shadow values for extended theme
const customShadows = {
  modal: '0px 8px 24px rgba(34, 34, 34, 0.15)',
  backdrop: 'rgba(0, 0, 0, 0.5)',
}

// Common dimensions from audit
const dimensions = {
  width: {
    full: '100%',
    auto: 'auto',
    badge: '120px', // max width for badges
    formContainer: '740px', // max width for form containers
    confirmationModal: '801px', // max width for confirmation modal
    pageContainer: '1360px', // max width for page containers
    mobileMin: '375px', // min width mobile
    desktopFormMin: '500px', // min width desktop forms
  },
  height: {
    auto: 'auto',
    full: '100%',
    input: '40px', // common input height
    icon: '26px', // common icon size
  },
} as const

// Z-index values for layering
const zIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const

// Transitions for consistent animations
const transitions = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
} as const

// Additional colors found in audit (not in palette)
const additionalColors = {
  gray: {
    light: '#E9E9EA',
    medium: '#C4C4C4',
    dark: '#6E6E6E',
    darker: '#3B3B3B',
  },
  status: {
    // From audit findings
    primary: '#1976d2',
    success: '#33cf4d',
    neutral: '#a1a1a1',
  },
} as const

// Media queries from audit
const mediaQueries = {
  maxWidth650: '@media (max-width: 650px)',
  maxWidth960: '@media (max-width: 960px)',
} as const

// Common sx patterns identified in audit (for reference)
const commonSxPatterns = {
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullSize: {
    width: '100%',
    height: '100%',
  },
  textCenter: {
    textAlign: 'center',
  },
  pointer: {
    cursor: 'pointer',
  },
  disabledState: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
} as const

// Border styles from audit
const borders = {
  standard: `1px solid ${palette.black.plus70}`,
  thick: `2px solid ${palette.black.plus10}`,
  navigationActive: `inset 0px -4px 0px 0px ${palette.black.main}`,
  formContainerBottom: `inset 0px -1px 0px ${palette.black.plus70}`,
} as const

// Button styles refactored to use theme values
const buttonStyles = {
  MuiButton: {
    variants: [
      {
        props: { variant: 'primary' as const },
        style: {
          width: dimensions.width.badge, // Using theme dimension
          padding: spacing.padding.button, // Using theme spacing
          borderRadius: shape.borderRadiusVariants.small, // Using theme shape
          fontWeight: typography.fontWeight.semiBold, // Using theme typography
          fontSize: typography.fontSize.sm, // Using theme typography
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          border: 'none',
          outline: 'none',
          backgroundColor: palette.black.main,
          color: palette.black.plus100,
          '&:hover': {
            backgroundColor: palette.black.plus10,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.black.plus70,
            color: palette.black.plus100,
          },
        },
      },
      {
        props: { variant: 'secondary' as const },
        style: {
          width: dimensions.width.badge,
          padding: spacing.padding.button,
          borderRadius: shape.borderRadiusVariants.small,
          fontWeight: typography.fontWeight.semiBold,
          fontSize: typography.fontSize.sm,
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          outline: 'none',
          backgroundColor: palette.black.plus100,
          color: palette.black.main,
          border: borders.thick,
          '&:hover': {
            backgroundColor: palette.black.plus90,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.black.plus80,
            color: palette.black.plus40,
          },
        },
      },
      {
        props: { variant: 'error' as const },
        style: {
          width: dimensions.width.badge,
          padding: spacing.padding.button,
          borderRadius: shape.borderRadiusVariants.small,
          fontWeight: typography.fontWeight.semiBold,
          fontSize: typography.fontSize.sm,
          cursor: 'pointer',
          transition: 'background-color 0.2s, opacity 0.2s',
          border: 'none',
          outline: 'none',
          backgroundColor: palette.red.main,
          color: palette.black.plus100,
          '&:hover': {
            backgroundColor: palette.red.plus5,
          },
          '&:disabled': {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: palette.red.plus10,
            color: palette.black.plus100,
          },
        },
      },
    ],
  },
}

// Main theme options
const options: ThemeOptions = {
  typography: {
    fontFamily: typography.fontFamily,
    allVariants: typography.allVariants,
    h1: typography.h1,
    h2: typography.h2,
    h3: typography.h3,
    h4: typography.h4,
    body1: typography.body1,
    body2: typography.body2,
    button: typography.button,
    caption: typography.caption,
  },
  palette: {
    ...palette,
    // MUI expects grey with specific keys
    grey: {
      50: additionalColors.gray.light,
      100: additionalColors.gray.light,
      200: additionalColors.gray.light,
      300: additionalColors.gray.light,
      400: additionalColors.gray.medium,
      500: additionalColors.gray.medium,
      600: additionalColors.gray.dark,
      700: additionalColors.gray.dark,
      800: additionalColors.gray.darker,
      900: additionalColors.gray.darker,
      A100: additionalColors.gray.light,
      A200: additionalColors.gray.medium,
      A400: additionalColors.gray.dark,
      A700: additionalColors.gray.darker,
    },
  },
  breakpoints: {
    unit: 'px',
    values: {
      mobile: 0,
      tablet: 769,
      laptop: 1024,
      desktop: 1216,
      widescreen: 1408,
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape,
  shadows: muiShadows,
  zIndex,
  transitions,
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 0.85,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 0,
          },
        },
      },
    },
    // Add the custom work ticket button styles
    ...buttonStyles,
  },
}

// Type declarations remain the same as original theme
declare module '@mui/system' {
  interface BreakpointOverrides {
    xs: false
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true
    tablet: true
    laptop: true
    desktop: true
    widescreen: true
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    black: {
      main: string
      plus10: string
      plus20: string
      plus30: string
      plus40: string
      plus50: string
      plus60: string
      plus70: string
      plus80: string
      plus85: string
      plus90: string
      plus100: string
    }
    red: {
      minus5: string
      main: string
      plus5: string
      plus10: string
      plus40: string
      plus45: string
      plus80: string
    }
    supernova: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    green: {
      minus5: string
      main: string
      plus5: string
      plus55: string
      lineRun: string
    }
    blue: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    purple: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
  }

  interface PaletteOptions {
    black?: {
      main: string
      plus10: string
      plus20: string
      plus30: string
      plus40: string
      plus50: string
      plus60: string
      plus70: string
      plus80: string
      plus85: string
      plus90: string
      plus100: string
    }
    red?: {
      minus5: string
      main: string
      plus5: string
      plus10: string
      plus40: string
      plus45: string
      plus80: string
    }
    supernova?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    green?: {
      minus5: string
      main: string
      plus5: string
      plus55: string
      lineRun: string
    }
    blue?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
    purple?: {
      minus10: string
      main: string
      plus10: string
      plus40: string
    }
  }
}

// Add custom variants to Material UI
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true
    secondary: true
    error: true
  }
}

// Create the comprehensive theme with custom extensions
export const comprehensiveTheme = createTheme(options)

// Add custom typography properties to the theme
comprehensiveTheme.typography.fontSize = typography.fontSize
comprehensiveTheme.typography.fontWeight = typography.fontWeight
comprehensiveTheme.typography.lineHeight = typography.lineHeight

// Add custom properties that don't fit into standard MUI theme structure
const extendedTheme = {
  ...comprehensiveTheme,
  dimensions,
  commonSxPatterns,
  borders,
  customSpacing: spacing,
  additionalColors,
  mediaQueries,
  customShadows,
}

export default extendedTheme

// Export individual parts for direct import if needed
export {
  additionalColors,
  borders,
  commonSxPatterns,
  customShadows,
  dimensions,
  mediaQueries,
  muiShadows,
  shape,
  spacing,
  transitions,
  typography,
  zIndex,
}

// Export helper functions for theme usage
export const getSpacing = (value: keyof typeof spacing.padding | keyof typeof spacing.margin) => {
  return (
    spacing.padding[value as keyof typeof spacing.padding] ||
    spacing.margin[value as keyof typeof spacing.margin]
  )
}

export const getBorderRadius = (variant: keyof typeof shape.borderRadiusVariants = 'small') => {
  return shape.borderRadiusVariants[variant]
}

export const getFontSize = (size: keyof typeof typography.fontSize = 'sm') => {
  return typography.fontSize[size]
}

export const getFontWeight = (weight: keyof typeof typography.fontWeight = 'regular') => {
  return typography.fontWeight[weight]
}

export const getLineHeight = (height: keyof typeof typography.lineHeight = 'normal') => {
  return typography.lineHeight[height]
}

/*
 * COMPREHENSIVE THEME DOCUMENTATION
 * =================================
 *
 * This theme file captures ALL hardcoded values found during the Phase 1 audit of styled() and sx usage.
 *
 * Usage Examples:
 * --------------
 *
 * 1. Replace hardcoded colors:
 *    Before: sx={{ color: '#222222' }}
 *    After:  sx={{ color: 'black.main' }}
 *
 * 2. Replace hardcoded spacing:
 *    Before: sx={{ padding: '8px 16px' }}
 *    After:  sx={{ padding: theme.customSpacing.padding.button }}
 *
 * 3. Replace hardcoded typography:
 *    Before: sx={{ fontSize: '14px', fontWeight: 600 }}
 *    After:  sx={{ ...theme.typography.button }}
 *
 * 4. Replace hardcoded dimensions:
 *    Before: sx={{ maxWidth: '740px' }}
 *    After:  sx={{ maxWidth: theme.dimensions.width.formContainer }}
 *
 * 5. Use common patterns:
 *    Before: sx={{ display: 'flex', flexDirection: 'column' }}
 *    After:  sx={{ ...theme.commonSxPatterns.flexColumn }}
 *
 * Where Values Are Currently Used:
 * --------------------------------
 *
 * Colors:
 * - #222222 (black.main): Primary UI color, text, buttons
 * - #3B3B3B (black.plus10): Hover states, borders
 * - #FFFFFF (black.plus100): White backgrounds, text on dark
 * - #FF0018 (red.main): Error states, destructive actions
 * - #D4D4D4 (black.plus70): Disabled states, borders
 * - #C4C4C4 (additionalColors.gray.medium): Custom gray usage
 * - rgba(34, 34, 34, 0.15): Modal shadows
 *
 * Typography:
 * - 12px: Small text, error messages, notes
 * - 14px: Default body text (MOST COMMON)
 * - 16px: Navigation, larger text
 * - 24px: H1 headings
 * - 26px: Icon sizes
 *
 * Spacing:
 * - padding: '8px 16px': Badges, tabs, buttons (MOST COMMON)
 * - padding: '16px 24px': Modals
 * - padding: '20px 24px': Navigation
 * - marginTop: '16px': Common vertical spacing
 *
 * Border Radius:
 * - 4px: Default for buttons, inputs (MOST COMMON)
 * - 6px: Tab containers
 * - 8px: Modals, form containers
 * - 44px: Badges/pills
 *
 * This theme file should be the single source of truth for all design tokens.
 * In Phase 2, all hardcoded values will be replaced with references to this theme.
 */
